import React from 'react';
import {
  Box,
  Toolbar,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, usePermissions } from '../contexts/AuthContext';
import { useSignOut } from '../auth/useSignOut';
import { useThemeMode } from '../contexts/ThemeContext';
import { useNavigationGuard } from '../contexts/NavigationGuardContext';
import { TopBar } from './TopBar';
import { Sidebar } from './Sidebar';
import MobileBottomNav from './MobileBottomNav';
import { visibleNav, GROUP_LABELS } from './nav';
import {
  DRAWER_WIDTH,
  DRAWER_WIDTH_COLLAPSED,
  MOBILE_NAV_MAX_WIDTH,
  MOBILE_TAB_BAR_HEIGHT,
} from './constants';

const drawerWidth = DRAWER_WIDTH;
const collapsedDrawerWidth = DRAWER_WIDTH_COLLAPSED;

interface ShellProps {
  children: React.ReactNode;
}

export function Shell({ children }: ShellProps) {
  const { user } = useAuth();
  const signOut = useSignOut();
  const { permissions } = usePermissions();
  const { isDarkMode, toggleTheme } = useThemeMode();
  const theme = useTheme();
  const location = useLocation();
  const navGuard = useNavigationGuard();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));
  // Phone widths get the global bottom tab bar (Home/Time/Away/Me).
  const showMobileNav = useMediaQuery(`(max-width: ${MOBILE_NAV_MAX_WIDTH}px)`);
  const [manualCollapse, setManualCollapse] = React.useState<boolean | null>(null);
  const isCollapsed = manualCollapse ?? isMediumScreen;
  const activeDrawerWidth = isCollapsed ? collapsedDrawerWidth : drawerWidth;
  const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [pendingNavPath, setPendingNavPath] = React.useState<string | null>(null);

  const navSections = React.useMemo(() => visibleNav(permissions), [permissions]);

  // Derive breadcrumb: "{Group} · {item label}" based on active route
  const activeItem = navSections.flatMap((s) => s.items).find((i) => i.path === location.pathname);
  const crumb = activeItem
    ? `${GROUP_LABELS[activeItem.group]} · ${activeItem.label}`
    : undefined;

  const handleNavClick = React.useCallback((e: React.MouseEvent, path: string) => {
    if (navGuard.isBlocked() && location.pathname !== path) {
      e.preventDefault();
      setPendingNavPath(path);
    }
  }, [navGuard, location.pathname]);

  const handleDrawerToggle = () => setMobileOpen((o) => !o);

  const handleCollapseToggle = React.useCallback(() => {
    setManualCollapse((prev) => (prev === null ? !isMediumScreen : !prev));
  }, [isMediumScreen]);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);
  const handleLogout = () => { handleUserMenuClose(); signOut(); };

  return (
    <Box sx={{ display: 'flex' }}>
      <TopBar
        isDarkMode={isDarkMode}
        toggleTheme={toggleTheme}
        user={user}
        userMenuAnchor={userMenuAnchor}
        activeDrawerWidth={activeDrawerWidth}
        crumb={crumb}
        onDrawerToggle={handleDrawerToggle}
        onUserMenuOpen={handleUserMenuOpen}
        onUserMenuClose={handleUserMenuClose}
        onLogout={handleLogout}
      />

      <Sidebar
        isCollapsed={isCollapsed}
        mobileOpen={mobileOpen}
        activeDrawerWidth={activeDrawerWidth}
        navSections={navSections}
        onDrawerToggle={handleDrawerToggle}
        onCollapseToggle={handleCollapseToggle}
        onNavClick={handleNavClick}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          position: 'relative',
          width: { xs: '100%', sm: `calc(100% - ${activeDrawerWidth}px)` },
          maxWidth: '100%',
          transition: 'width 0.2s ease-out',
          minHeight: '100vh',
          overflowX: 'hidden',
          bgcolor: 'background.default',
          pt: { xs: 2, sm: '22px' },
          px: { xs: 2, sm: '28px' },
          // `pb` is the sole padding-bottom source (no `p` shorthand to override
          // it at the sm breakpoint), so the phone-width clearance for the fixed
          // tab bar always wins and page content is never hidden behind it.
          pb: showMobileNav
            ? `calc(env(safe-area-inset-bottom, 0px) + ${MOBILE_TAB_BAR_HEIGHT + 24}px)`
            : { xs: 2, sm: '60px' },
        }}
      >
        {/* Spacer to clear the fixed AppBar */}
        <Toolbar sx={{ minHeight: '56px !important' }} />

        {/* Soft 12px gradient fade from surface-2-faint → transparent at top of content */}
        <Box
          aria-hidden
          sx={(t) => ({
            position: 'absolute',
            top: '56px',
            left: 0,
            right: 0,
            height: '12px',
            background: `linear-gradient(to bottom, ${t.palette.surface.faint}, transparent)`,
            pointerEvents: 'none',
          })}
        />

        <Container maxWidth={false} disableGutters>
          {children}
        </Container>
      </Box>

      {/* Global phone-width bottom navigation (Home / Time / Away / Me). */}
      {showMobileNav && <MobileBottomNav activeDrawerWidth={activeDrawerWidth} />}

      {/* Unsaved changes dialog */}
      <Dialog open={!!pendingNavPath} onClose={() => setPendingNavPath(null)}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Would you like to save them before leaving?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingNavPath(null)}>Cancel</Button>
          <Button
            color="warning"
            onClick={() => {
              const path = pendingNavPath;
              const callbacks = navGuard.getCallbacks();
              callbacks?.discard();
              setPendingNavPath(null);
              if (path) navigate(path);
            }}
          >
            Discard &amp; Leave
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              const path = pendingNavPath;
              const callbacks = navGuard.getCallbacks();
              setPendingNavPath(null);
              if (callbacks && (await callbacks.save()) === false) return;
              if (path) navigate(path);
            }}
          >
            Save &amp; Leave
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
